import { supabase } from './supabase';
import { generateJoinCode } from './joinCode';

export type MembershipRole = 'admin' | 'big' | 'little';
export type JoinRole = 'big' | 'little';
export type MembershipStatus = 'pending' | 'approved' | 'rejected';

export interface Group {
  id: string;
  name: string;
  school: string;
  description: string;
  join_code: string;
  blind_rankings: boolean;
  max_littles_per_big?: 1 | 2 | 3;
  min_big_rankings: number;
  min_little_rankings: number;
  ranking_deadline: string | null;
  ranking_deadline_at?: string | null;
  ranking_deadline_timezone?: string | null;
  reveal_email_subject: string | null;
  reveal_email_body: string | null;
  created_by: string;
  owner_id: string;
  active_cycle_id: string | null;
  created_at: string;
}

// Kept in sync with supabase/functions/send-pairing-reveal/index.ts —
// the Edge Function falls back to these when the chapter's own template
// fields are null, so the Settings preview shows what a Big would
// actually receive by default.
export const DEFAULT_REVEAL_SUBJECT = 'Your Little{{little_plural_s}} {{little_plural_is_are}} here — {{chapter_name}}';
export const DEFAULT_REVEAL_BODY = `Hi {{first_name}},

Your Little{{little_plural_s}} {{little_plural_is_are}}: {{little_names}}

Said with love,
{{chapter_name}}`;

export interface Cycle {
  id: string;
  group_id: string;
  label: string;
  started_at: string;
  ended_at: string | null;
  created_by: string | null;
  created_at: string;
}

export interface Membership {
  id: string;
  group_id: string;
  user_id: string;
  role: MembershipRole;
  status: MembershipStatus;
  willing_to_take_twins: boolean;
  wanted_little_count?: 2 | 3 | null;
  requested_role: MembershipRole | null;
  is_admin: boolean;
  created_at: string;
}

export interface MembershipWithGroup extends Membership {
  group: Group;
}

export interface Profile {
  avatar_url?: string | null;
  email: string;
  name: string | null;
}

export interface PendingMembership extends Membership {
  profile: Profile | null;
}

// Admin access is a flag independent of role now — role='admin' (legacy,
// admin-only membership) always carries it, but a big/little can be granted
// it too without giving up their big/little role.
export function isEffectiveAdmin(membership: { role: MembershipRole; is_admin: boolean }): boolean {
  return membership.role === 'admin' || membership.is_admin;
}

// Groups created before the school column existed default to an empty
// string there, so include the university only when it is available.
export function groupLabel(group: { name: string; school: string }): string {
  return group.school ? `${group.name} at ${group.school}` : group.name;
}

const MAX_JOIN_CODE_ATTEMPTS = 5;

export async function createGroup(
  name: string,
  school: string
): Promise<{ group: Group | null; error: string | null }> {
  for (let attempt = 0; attempt < MAX_JOIN_CODE_ATTEMPTS; attempt++) {
    const joinCode = generateJoinCode();
    const { data, error } = await supabase.rpc('create_group', {
      p_name: name,
      p_school: school,
      p_join_code: joinCode,
    });
    if (!error) {
      return { group: data as Group, error: null };
    }
    if (error.code === '23505') continue; // join_code collision, retry with a fresh one
    return { group: null, error: error.message };
  }
  return { group: null, error: 'Could not generate a unique join code. Please try again.' };
}

export async function findGroupByJoinCode(
  code: string
): Promise<{ group: { id: string; name: string; school: string } | null; error: string | null }> {
  const { data, error } = await supabase.rpc('find_group_by_code', { p_code: code.trim().toUpperCase() });
  if (error) return { group: null, error: error.message };
  const match = Array.isArray(data) ? data[0] : null;
  if (!match) return { group: null, error: 'No group found with that code.' };
  return { group: match, error: null };
}

type JoinResult = { membership: Membership | null; error: string | null };
const joiningMemberships = new Map<string, Promise<JoinResult>>();
const JOIN_FAILURE = 'Could not confirm your chapter membership. Please try again. If this continues, contact your chapter admin.';

export async function requestToJoinGroup(
  groupId: string,
  role: MembershipRole
): Promise<JoinResult> {
  if (role !== 'big' && role !== 'little') {
    return { membership: null, error: 'Join as a Big or Little. An existing admin can grant admin access after you join.' };
  }
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) return { membership: null, error: 'Please sign in again to join your chapter.' };
    const key = `${user.id}:${groupId}`;
    const pending = joiningMemberships.get(key);
    if (pending) return await pending;
    const task = (async (): Promise<JoinResult> => {
      // Reuse every existing status, including rejected. Joining must never
      // change a role, restore approval, or strip existing admin privileges.
      const readMembership = () => supabase.from('memberships').select('*')
        .eq('group_id', groupId).eq('user_id', user.id).maybeSingle();
      const existing = await readMembership();
      if (existing.error) return { membership: null, error: JOIN_FAILURE };
      if (existing.data) return { membership: existing.data as Membership, error: null };
      const { error } = await supabase.from('memberships').insert({
        group_id: groupId, user_id: user.id, role, status: 'pending',
      });
      // Another tab or signup's pending-action handler may have won the insert.
      // The unique constraint remains the authority; only recover our own row.
      if (error && error.code !== '23505') return { membership: null, error: JOIN_FAILURE };
      const saved = await readMembership();
      return saved.error || !saved.data
        ? { membership: null, error: JOIN_FAILURE }
        : { membership: saved.data as Membership, error: null };
    })();
    joiningMemberships.set(key, task);
    try { return await task; }
    finally { if (joiningMemberships.get(key) === task) joiningMemberships.delete(key); }
  } catch {
    return { membership: null, error: JOIN_FAILURE };
  }
}

export async function getMyMemberships(): Promise<{
  memberships: MembershipWithGroup[];
  error: string | null;
}> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return { memberships: [], error: null };

  const { data, error } = await supabase
    .from('memberships')
    .select('*, group:groups(*)')
    .eq('user_id', user.id)
    .order('created_at', { ascending: true });

  if (error) return { memberships: [], error: error.message };
  return { memberships: (data ?? []) as unknown as MembershipWithGroup[], error: null };
}

export async function getPendingMemberships(
  groupId: string
): Promise<{ memberships: PendingMembership[]; error: string | null }> {
  const { data, error } = await supabase
    .from('memberships')
    .select('*, profile:profiles(email, name)')
    .eq('group_id', groupId)
    .eq('status', 'pending')
    .order('created_at', { ascending: true });

  if (error) return { memberships: [], error: error.message };
  return { memberships: (data ?? []) as unknown as PendingMembership[], error: null };
}

export async function updateMembershipStatus(
  membershipId: string,
  status: 'approved' | 'rejected'
): Promise<{ error: string | null }> {
  // Routed through a SECURITY DEFINER function (not a raw .update()) so
  // approving a request for role='admin' also sets is_admin — otherwise
  // the approved member gets admin nav links that silently fail, since
  // is_group_admin() checks is_admin, not role.
  const { error } = await supabase.rpc('approve_membership', {
    p_membership_id: membershipId,
    p_approve: status === 'approved',
  });
  return { error: error ? error.message : null };
}

export async function updateGroupProfile(
  groupId: string,
  name: string,
  school: string
): Promise<{ error: string | null }> {
  const { error } = await supabase.from('groups').update({ name, school }).eq('id', groupId).select('id').single();
  return { error: error ? error.message : null };
}

export async function updateGroupDescription(groupId: string, description: string): Promise<{ error: string | null }> {
  const { error } = await supabase.from('groups').update({ description }).eq('id', groupId).select('id').single();
  return { error: error ? error.message : null };
}

export async function updateGroupDeadline(groupId: string, deadline: string | null, timeZone: string | null): Promise<{ error: string | null }> {
  const { error } = await supabase.rpc('set_ranking_deadline', { p_group_id: groupId, p_deadline: deadline, p_timezone: deadline ? timeZone : null });
  return { error: error ? error.message : null };
}

// Every past and present cycle for a group, newest first — any member can
// see the list (RLS), though only rankings/pairings tied to a cycle carry
// the actual data.
export async function getGroupCycles(groupId: string): Promise<{ cycles: Cycle[]; error: string | null }> {
  const { data, error } = await supabase
    .from('cycles')
    .select('*')
    .eq('group_id', groupId)
    .order('started_at', { ascending: false });
  if (error) return { cycles: [], error: error.message };
  return { cycles: (data ?? []) as Cycle[], error: null };
}

// Closes whatever cycle is currently active (if any) and starts a new
// one, atomically — see start_cycle() in supabase/migrations for why this
// is a security-definer RPC rather than a couple of raw .update() calls.
export async function startCycle(groupId: string, label: string): Promise<{ cycle: Cycle | null; error: string | null }> {
  const { data, error } = await supabase.rpc('start_cycle', { p_group_id: groupId, p_label: label });
  if (error) return { cycle: null, error: error.message };
  return { cycle: data as Cycle, error: null };
}

export async function getApprovedRoleCounts(
  groupId: string
): Promise<{ bigs: number; littles: number; error: string | null }> {
  const { data, error } = await supabase
    .from('memberships')
    .select('role')
    .eq('group_id', groupId)
    .eq('status', 'approved')
    .in('role', ['big', 'little']);

  if (error) return { bigs: 0, littles: 0, error: error.message };
  const bigs = (data ?? []).filter(r => r.role === 'big').length;
  const littles = (data ?? []).filter(r => r.role === 'little').length;
  return { bigs, littles, error: null };
}

export async function updateGroupSettings(
  groupId: string,
  minBigRankings: number,
  minLittleRankings: number
): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from('groups')
    .update({ min_big_rankings: minBigRankings, min_little_rankings: minLittleRankings })
    .eq('id', groupId).select('id').single();
  return { error: error ? error.message : null };
}

export async function updateMaxLittlesPerBig(groupId: string, maximum: number): Promise<{ error: string | null }> {
  const { error } = await supabase.rpc('set_max_littles_per_big', { p_group_id: groupId, p_maximum: maximum });
  return { error: error ? error.message : null };
}

export async function setMyLittlePreference(groupId: string, willing: boolean, wantedCount: 2 | 3 | null): Promise<{ error: string | null }> {
  const { error } = await supabase.rpc('set_my_little_preference', {
    p_group_id: groupId, p_willing: wantedCount === null && willing, p_wanted_count: wantedCount,
  });
  return { error: error ? error.message : null };
}

export async function requestRoleChange(
  groupId: string,
  requestedRole: MembershipRole
): Promise<{ error: string | null }> {
  const { error } = await supabase.rpc('request_role_change', {
    p_group_id: groupId,
    p_requested_role: requestedRole,
  });
  return { error: error ? error.message : null };
}

export interface RoleChangeRequest extends Membership {
  profile: Profile | null;
}

export async function getPendingRoleChanges(
  groupId: string
): Promise<{ requests: RoleChangeRequest[]; error: string | null }> {
  const { data, error } = await supabase
    .from('memberships')
    .select('*, profile:profiles(email, name)')
    .eq('group_id', groupId)
    .eq('status', 'approved')
    .not('requested_role', 'is', null);

  if (error) return { requests: [], error: error.message };
  return { requests: (data ?? []) as unknown as RoleChangeRequest[], error: null };
}

export async function resolveRoleChange(
  membershipId: string,
  approve: boolean,
  requestedRole: MembershipRole
): Promise<{ error: string | null }> {
  // Same reasoning as approve_membership — approving a change to
  // role='admin' must also grant is_admin, or the member ends up with
  // admin nav links but no actual admin access.
  const { error } = await supabase.rpc('resolve_role_change', {
    p_membership_id: membershipId,
    p_approve: approve,
    p_requested_role: requestedRole,
  });
  return { error: error ? error.message : null };
}

export interface GroupAdmin {
  user_id: string;
  role: MembershipRole;
  profile: Profile | null;
}

export async function getGroupAdmins(groupId: string): Promise<{ admins: GroupAdmin[]; error: string | null }> {
  const { data, error } = await supabase
    .from('memberships')
    .select('user_id, role, profile:profiles(email, name)')
    .eq('group_id', groupId)
    .eq('is_admin', true)
    .eq('status', 'approved');

  if (error) return { admins: [], error: error.message };
  return { admins: (data ?? []) as unknown as GroupAdmin[], error: null };
}

export interface GroupMember extends Membership {
  profile: Profile | null;
}

// Every approved member of a group, any role — used to grant/revoke admin
// access on someone without touching their big/little role.
export async function getGroupMembers(groupId: string): Promise<{ members: GroupMember[]; error: string | null }> {
  const { data, error } = await supabase
    .from('memberships')
    .select('*, profile:profiles(email, name)')
    .eq('group_id', groupId)
    .eq('status', 'approved');

  if (error) return { members: [], error: error.message };
  return { members: (data ?? []) as unknown as GroupMember[], error: null };
}

export async function setMemberAdmin(membershipId: string, isAdmin: boolean): Promise<{ error: string | null }> {
  const { error } = await supabase.rpc('set_member_admin', {
    p_membership_id: membershipId,
    p_is_admin: isAdmin,
  });
  return { error: error ? error.message : null };
}

// Removes an approved member from the chapter (they graduated, left, etc.)
// without touching their Sorora account — only this chapter's data about
// them. Blocked server-side if they're the owner (transfer first).
export async function removeMember(membershipId: string): Promise<{ error: string | null }> {
  const { error } = await supabase.rpc('remove_member', { p_membership_id: membershipId });
  return { error: error ? error.message : null };
}

export async function transferGroupOwnership(
  groupId: string,
  newOwnerId: string
): Promise<{ group: Group | null; error: string | null }> {
  const { data, error } = await supabase.rpc('transfer_group_ownership', {
    p_group_id: groupId,
    p_new_owner_id: newOwnerId,
  });
  if (error) return { group: null, error: error.message };
  return { group: data as Group, error: null };
}

export async function updateBlindRankings(groupId: string, blind: boolean): Promise<{ error: string | null }> {
  const { error } = await supabase.from('groups').update({ blind_rankings: blind }).eq('id', groupId).select('id').single();
  return { error: error?.message ?? null };
}

// `null` on either field clears the chapter's override so the Edge
// Function's default fires again — i.e. "Reset to default" in the UI
// sends { subject: null, body: null }.
export async function updateRevealEmailTemplate(
  groupId: string,
  subject: string | null,
  body: string | null,
): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from('groups')
    .update({ reveal_email_subject: subject, reveal_email_body: body })
    .eq('id', groupId).select('id').single();
  return { error: error?.message ?? null };
}

// Same tag semantics as the Edge Function's renderTemplate — kept small
// and dependency-free so the Settings preview matches what a Big
// actually gets.
export function renderRevealTemplate(
  template: string,
  vars: { first_name: string; little_names: string; chapter_name: string; little_count: number },
): string {
  const s = vars.little_count > 1 ? 's' : '';
  const isAre = vars.little_count > 1 ? 'are' : 'is';
  return template
    .replace(/\{\{\s*first_name\s*\}\}/g, vars.first_name)
    .replace(/\{\{\s*little_names\s*\}\}/g, vars.little_names)
    .replace(/\{\{\s*chapter_name\s*\}\}/g, vars.chapter_name)
    .replace(/\{\{\s*little_plural_s\s*\}\}/g, s)
    .replace(/\{\{\s*little_plural_is_are\s*\}\}/g, isAre);
}

// Admins change only their own matching role, without an approval request.
export async function setMyAdminMatchingRole(groupId: string, role: 'admin' | 'big'): Promise<{ error: string | null }> {
  const { error } = await supabase.rpc('set_my_admin_matching_role', { p_group_id: groupId, p_role: role });
  return { error: error?.message ?? null };
}
